import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, message, SubContent, Table } from '@maxtropy/components';
import {
  apiV2MicrogridTopologyThermalParamDetailPost,
  apiV2MicrogridTopologyThermalParamSavePost,
} from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Col, Row, Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/hooks';
import ConfigThermalPowerModal from './ConfigThermalPowerModal';
import styles from './index.module.scss';
import { ThermalPowerConfig, ThermalPowerLimitFormValues, ThermalPowerOutputRange } from './types';

export interface ThermalPowerParamsTabRef {
  save: () => Promise<void>;
  isEditing: boolean;
  loading: boolean;
}

const formatRate = (value?: number) => (value === undefined ? '' : value.toFixed(2));

const formatPowerSplitLine = (value?: number) => {
  if (value === undefined) {
    return '';
  }

  return value > 10000 ? `${value / 10000}万MW` : `${value}MW`;
};

const buildOutputRanges = (config: ThermalPowerConfig): ThermalPowerOutputRange[] => [
  {
    id: 'above',
    outputRange: `大于${formatPowerSplitLine(config.powerSplitLine)}（出力分隔线）`,
    adjustableAmount: `${formatRate(config.aboveMinAdjustRate)}～${formatRate(config.aboveMaxAdjustRate)} MW/min`,
  },
  {
    id: 'below',
    outputRange: `小于${formatPowerSplitLine(config.powerSplitLine)}（出力分隔线）`,
    adjustableAmount: `${formatRate(config.belowMinAdjustRate)}～${formatRate(config.belowMaxAdjustRate)} MW/min`,
  },
];

const ThermalPowerParamsTab = forwardRef<ThermalPowerParamsTabRef>((_, ref) => {
  const hasSetupPermission = useHasPermission(PermissionsType.B_THERMAL_POWER_SIDE_PARAMETER_CONFIGURATION);
  const [form] = Form.useForm<ThermalPowerLimitFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [config, setConfig] = useState<ThermalPowerConfig>({});
  const [modalOpen, setModalOpen] = useState(false);
  const { refreshAsync: refreshThermalParamDetail, loading: detailLoading } = useRequest(() =>
    apiV2MicrogridTopologyThermalParamDetailPost({}).then(res => {
      setConfig({
        powerSplitLine: res.powerSplitLine,
        belowMinAdjustRate: res.belowSplitMinRate,
        belowMaxAdjustRate: res.belowSplitMaxRate,
        aboveMinAdjustRate: res.aboveSplitMinRate,
        aboveMaxAdjustRate: res.aboveSplitMaxRate,
      });
      form.setFieldsValue({
        totalPowerLimit: res.maxTotalPower,
        singleGeneratorPowerLimit: res.minSinglePower,
      });
    })
  );
  const { runAsync: saveThermalParam, loading: saving } = useRequest(apiV2MicrogridTopologyThermalParamSavePost, {
    manual: true,
  });

  const dataSource = useMemo(() => buildOutputRanges(config), [config]);

  const columns: ColumnsType<ThermalPowerOutputRange> = useMemo(
    () => [
      {
        title: '出力区间（单台发电机组的发电功率）',
        dataIndex: 'outputRange',
      },
      {
        title: '单台发电机组每分钟可调节量(上升/下降)',
        dataIndex: 'adjustableAmount',
      },
    ],
    []
  );

  const handleConfigOk = (values: ThermalPowerConfig) => {
    setConfig(values);
    setModalOpen(false);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    if (
      values.totalPowerLimit !== undefined &&
      values.singleGeneratorPowerLimit !== undefined &&
      values.totalPowerLimit <= values.singleGeneratorPowerLimit
    ) {
      form.setFields([
        {
          name: 'totalPowerLimit',
          errors: ['总发电功率上限需大于单台发电功率下限'],
        },
      ]);
      return;
    }

    await saveThermalParam({
      powerSplitLine: config.powerSplitLine,
      belowSplitMinRate: config.belowMinAdjustRate,
      belowSplitMaxRate: config.belowMaxAdjustRate,
      aboveSplitMinRate: config.aboveMinAdjustRate,
      aboveSplitMaxRate: config.aboveMaxAdjustRate,
      maxTotalPower: values.totalPowerLimit,
      minSinglePower: values.singleGeneratorPowerLimit,
    });
    await refreshThermalParamDetail();
    message.success('保存成功');
    setIsEditing(false);
  };

  const handleCancel = async () => {
    await refreshThermalParamDetail();
    setIsEditing(false);
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isEditing,
    loading: detailLoading || saving,
  }));

  return (
    <>
      <Button
        type="primary"
        htmlType="button"
        wrapStyle={{ marginBottom: 10 }}
        disabled={!isEditing}
        onClick={() => setModalOpen(true)}
      >
        配置
      </Button>
      <Table rowKey="id" loading={detailLoading} pagination={false} columns={columns} dataSource={dataSource} />
      <SubContent title="火电出力限制" className={styles.limitContent}>
        <Form
          form={form}
          layout="vertical"
          disabled={!isEditing}
          initialValues={{
            totalPowerLimit: undefined,
            singleGeneratorPowerLimit: undefined,
          }}
        >
          <Row gutter={96}>
            <Col span={12}>
              <Form.Item
                label="总发电功率-上限"
                name="totalPowerLimit"
                tooltip={{
                  title: '火电整体的发电功率上限',
                  icon: <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)' }} />,
                }}
                rules={[{ required: true, message: '请输入' }]}
              >
                <InputNumber placeholder="请输入" addonAfter="MW" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="单台发电功率-下限"
                name="singleGeneratorPowerLimit"
                tooltip={{
                  title: '任意发电机组的发电功率下限',
                  icon: <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)' }} />,
                }}
                rules={[{ required: true, message: '请输入' }]}
              >
                <InputNumber placeholder="请输入" addonAfter="MW" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </SubContent>
      <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 0, marginTop: 16 }}>
        {isEditing ? (
          <>
            <Button type="primary" htmlType="button" loading={saving} disabled={false} onClick={handleSave}>
              保存
            </Button>
            <Button htmlType="button" disabled={false} onClick={handleCancel}>
              取消
            </Button>
          </>
        ) : (
          hasSetupPermission && (
            <Button type="primary" htmlType="button" disabled={false} onClick={() => setIsEditing(true)}>
              编辑
            </Button>
          )
        )}
      </Space>
      <ConfigThermalPowerModal
        open={modalOpen}
        values={config}
        onCancel={() => setModalOpen(false)}
        onOk={handleConfigOk}
      />
    </>
  );
});

export default ThermalPowerParamsTab;
